import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import { formatTimeRange, formatDateMonthDay } from '../../utils/timeFormat';
import { resolveProfilePhoto } from '../../utils/profilePhoto';
import { 
  ArrowLeft, Calendar, MapPin, Clock, Users, CheckCircle, AlertCircle,
  RefreshCcw, Eye, MessageSquare, UserMinus, Bell, Briefcase, Loader2,
  ChevronRight, Star, XCircle
} from 'lucide-react';

const BookingManagementDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [gigs, setGigs] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ upcoming: 0, in_progress: 0, completed: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [pendingConfirmations, setPendingConfirmations] = useState([]);
  
  // Replace staff state
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [replaceBookingId, setReplaceBookingId] = useState(null);
  const [replaceTalentName, setReplaceTalentName] = useState('');
  const [replaceReason, setReplaceReason] = useState('');
  const [replacing, setReplacing] = useState(false);

  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchDashboard();
    fetchPendingConfirmations();
  }, [activeTab]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get('/booking-management/dashboard', {
        params: { status: activeTab, limit: 20 }
      });
      setGigs(response.data.gigs || []);
      setStatusCounts(response.data.status_counts || {});
    } catch (error) {
      toast.error('Failed to load booking dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingConfirmations = async () => {
    try {
      const response = await api.get('/booking-management/pending-confirmations');
      setPendingConfirmations(response.data.pending || []);
    } catch (error) {
      console.error('Failed to load pending confirmations:', error);
    }
  };

  const handleReplaceStaff = async () => {
    if (!replaceBookingId) return;
    setReplacing(true);
    try {
      await api.post('/booking-management/replace-staff', {
        booking_id: replaceBookingId,
        reason: replaceReason
      });
      toast.success('Staff replaced. You can now book a replacement.');
      setShowReplaceDialog(false);
      setReplaceBookingId(null);
      setReplaceTalentName('');
      setReplaceReason('');
      fetchDashboard();
      fetchPendingConfirmations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to replace staff');
    } finally {
      setReplacing(false);
    }
  };

  const sendReminder = async (bookingId) => {
    try {
      await api.post(`/booking-management/send-reminder/${bookingId}`);
      toast.success('Reminder sent');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'in_progress': return 'bg-green-100 text-green-700 border-green-200';
      case 'completed': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Booking Management</h1>
                <p className="text-sm text-slate-500">Manage your gigs and assigned talent</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => { fetchDashboard(); fetchPendingConfirmations(); }}>
              <RefreshCcw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Pending Confirmations Alert */}
        {pendingConfirmations.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-500" />
                  <div>
                    <h3 className="font-medium text-amber-800">
                      {pendingConfirmations.length} Pending Confirmation{pendingConfirmations.length > 1 ? 's' : ''}
                    </h3>
                    <p className="text-sm text-amber-600">
                      {pendingConfirmations.filter(p => p.is_urgent).length} urgent (less than 12 hours)
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {pendingConfirmations.slice(0, 3).map(pc => (
                    <div key={pc.booking_id} className="flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-amber-200">
                      <span className="text-sm font-medium text-slate-700">{pc.talent_name}</span>
                      <Badge className={pc.is_urgent ? 'bg-red-500' : 'bg-amber-500'} variant="default">
                        {Math.round(pc.hours_remaining)}h
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => sendReminder(pc.booking_id)}
                        className="h-6 w-6 p-0"
                      >
                        <Bell className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border">
            <TabsTrigger value="upcoming" className="data-[state=active]:bg-blue-100">
              Upcoming ({statusCounts.upcoming || 0})
            </TabsTrigger>
            <TabsTrigger value="in_progress" className="data-[state=active]:bg-green-100">
              In Progress ({statusCounts.in_progress || 0})
            </TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-slate-100">
              Completed ({statusCounts.completed || 0})
            </TabsTrigger>
            <TabsTrigger value="cancelled" className="data-[state=active]:bg-red-100">
              Cancelled ({statusCounts.cancelled || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
              </div>
            ) : gigs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-700">No {activeTab.replace('_', ' ')} gigs</h3>
                  <p className="text-slate-500 mt-1">
                    {activeTab === 'upcoming' 
                      ? 'Create a new gig to get started' 
                      : `You don't have any ${activeTab.replace('_', ' ')} gigs`}
                  </p>
                  {activeTab === 'upcoming' && (
                    <Link to="/client/shifts/create">
                      <Button className="mt-4 bg-[#03DFFC] hover:bg-[#03DFFC]/90">Create Gig</Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {gigs.map(gig => (
                  <Card key={gig.gig_id} className="overflow-hidden" data-testid={`gig-card-${gig.gig_id}`}>
                    <CardHeader className="pb-2 bg-slate-50 border-b">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{gig.venue_name}</CardTitle>
                          <CardDescription className="flex flex-wrap items-center gap-3 mt-1">
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" />
                              {gig.role_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDateMonthDay(gig.shift_date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {formatTimeRange(gig.start_time, gig.end_time)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {gig.city}, {gig.state}
                            </span>
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`border ${getStatusColor(gig.gig_status)}`}>
                            {gig.gig_status.replace('_', ' ')}
                          </Badge>
                          <Link to={`/client/job-overview/${gig.gig_id}`}>
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-4">
                      {/* Staffing Summary */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-slate-400" />
                            <span className="text-sm">
                              <span className="font-medium">{gig.booked_count}</span>
                              <span className="text-slate-500"> / {gig.talent_needed} booked</span>
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="text-sm">
                              <span className="font-medium text-green-600">{gig.staffed_count ?? gig.confirmed_count ?? 0}</span>
                              <span className="text-slate-500"> staffed</span>
                            </span>
                          </div>
                          {gig.pending_confirmation > 0 && (
                            <div className="flex items-center gap-2">
                              <AlertCircle className="w-5 h-5 text-amber-500" />
                              <span className="text-sm">
                                <span className="font-medium text-amber-600">{gig.pending_confirmation}</span>
                                <span className="text-slate-500"> pending</span>
                              </span>
                            </div>
                          )}
                        </div>
                        {!gig.is_fully_staffed && gig.gig_status === 'upcoming' && (
                          <Link to={`/client/discover?gig=${gig.gig_id}`}>
                            <Button size="sm" className="bg-[#03DFFC] hover:bg-[#03DFFC]/90">
                              + Add Talent
                            </Button>
                          </Link>
                        )}
                      </div>

                      {/* Assigned Talent */}
                      {gig.assigned_talent.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-slate-700">Assigned Talent</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {gig.assigned_talent.map(talent => (
                              <div 
                                key={talent.booking_id}
                                className={`flex items-center justify-between p-3 rounded-lg border transition hover:ring-2 hover:ring-cyan-200 ${
                                  talent.is_confirmed ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
                                }`}
                                data-testid={`talent-row-${talent.booking_id}`}
                              >
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className="flex items-center gap-3 flex-1 cursor-pointer"
                                  data-testid={`talent-profile-open-${talent.talent_id || talent.booking_id}`}
                                  title="View talent profile"
                                  onClick={() => talent.talent_id && navigate(`/client/discover/${talent.talent_id}`)}
                                  onKeyDown={(e) => {
                                    if ((e.key === 'Enter' || e.key === ' ') && talent.talent_id) {
                                      navigate(`/client/discover/${talent.talent_id}`);
                                    }
                                  }}
                                >
                                  {(() => {
                                    const ph = resolveProfilePhoto(talent);
                                    return ph ? (
                                      <img
                                        src={ph}
                                        alt={talent.name}
                                        className="w-10 h-10 rounded-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                                        {talent.first_name?.[0]}{talent.last_name?.[0]}
                                      </div>
                                    );
                                  })()}
                                  <div>
                                    <p className="font-medium text-slate-900">{talent.name}</p>
                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                      {talent.average_rating && (
                                        <span className="flex items-center gap-0.5">
                                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                          {talent.average_rating.toFixed(1)}
                                        </span>
                                      )}
                                      <span className="capitalize">{talent.tier}</span>
                                      {talent.checked_in && <Badge className="bg-green-500 text-[10px]">In</Badge>}
                                      {talent.checked_out && <Badge className="bg-blue-500 text-[10px]">Out</Badge>}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  {talent.is_confirmed ? (
                                    <Badge className="bg-green-500">Confirmed</Badge>
                                  ) : (
                                    <>
                                      <Badge className="bg-amber-500">Pending</Badge>
                                      {talent.hours_until_deadline && (
                                        <span className="text-xs text-amber-600">{talent.hours_until_deadline}h left</span>
                                      )}
                                    </>
                                  )}
                                  
                                  {/* Actions */}
                                  {!talent.is_confirmed && talent.can_replace && gig.gig_status === 'upcoming' && (
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => sendReminder(talent.booking_id)}
                                        className="h-8 w-8 p-0"
                                        title="Send Reminder"
                                      >
                                        <Bell className="w-4 h-4 text-amber-500" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setReplaceBookingId(talent.booking_id);
                                          setReplaceTalentName(talent.name);
                                          setShowReplaceDialog(true);
                                        }}
                                        className="h-8 w-8 p-0"
                                        title="Replace"
                                      >
                                        <UserMinus className="w-4 h-4 text-red-500" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Replace Staff Dialog */}
      <Dialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <UserMinus className="w-5 h-5" />
              Replace Staff
            </DialogTitle>
            <DialogDescription>
              This will cancel <strong>{replaceTalentName}</strong>'s booking and free up the spot.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason (optional)</label>
            <textarea
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              placeholder="e.g., Did not confirm in time, Found better match, etc."
              value={replaceReason}
              onChange={(e) => setReplaceReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplaceDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReplaceStaff}
              disabled={replacing}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {replacing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Replace Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingManagementDashboard;
